/**
 * The §F5.2 sidebar's dropdown — **client correction D1/D2**, 2026-08-23.
 *
 * > *"In the Indicators pane the dropdown selects the ACTUAL INDICATOR (B.2.1 etc.), not the
 * > group … In the Layers pane the dropdowns select ACTUAL LAYERS."*
 *
 * ## What this replaces
 *
 * C3's `GroupSelect` chose a *group* — a tier, a domain — and rendered that group's rows
 * beneath it. IFDC's correction is that the group was never the thing a reader came to pick:
 * choosing "Outcome indicators" is a step on the way to choosing C.1.1, and a control that
 * stops one step short is a control the reader still has to scroll past. So the select now
 * carries the leaves, the groups become `<optgroup>` headings inside it — which is what an
 * optgroup is for — and the list of rows underneath is gone with the step it served.
 *
 * The objection C3's file raised (*"the rows are not the options"* — a row carried a
 * provenance chip, an overlay `+`, a lock, an opacity slider) is answered rather than
 * overruled. Everything that was a *decoration* is folded into the option's own text: a
 * demo-only indicator says `— DEMO`, a locked layer says which classification locks it. And
 * everything that was an *action on a thing already chosen* — the overlay's ✕, the layer's
 * opacity — moves under the select as a short list of what is **on**, which is a list of two
 * or three rather than one of twenty-seven.
 *
 * ## Why one component for both panes
 *
 * Four selects (indicator, overlay, Y, raster, vector — five) with one set of rules: a
 * placeholder that means *nothing chosen*, `<optgroup>`s in the server's order, options that
 * can be disabled **with a reason on them**, and a `lead` slot for the colour an `<option>`
 * cannot carry. Two implementations of that would be two chances for the Layers pane to
 * disable a row the Indicators pane would have offered.
 *
 * `fallback` is the case a filtered picker must not get wrong: the domain rail narrows the
 * options, and §F5.2 is explicit that filtering the *list* never changes the *map*. A mapped
 * indicator that the rail has filtered out is therefore still mapped — and a `<select>` whose
 * `value` matches no option silently renders as its first one, i.e. as a control claiming
 * nothing is mapped while the map is painted. So the current value is always rendered as an
 * option of its own when nothing else offers it. `CascadeFilter`'s `LevelSelect` solves the
 * same problem the same way for an option list that has not landed yet.
 */
import type { ReactNode } from 'react';

export interface PaneOption {
  value: string;
  label: string;
  /**
   * Offered but refused — a demo-only indicator (D1), a locked or unwired layer (D2). The
   * option stays in the list because a reader who cannot find an indicator at all concludes
   * the portal lost it; the label is where the reason goes, since a disabled `<option>`
   * fires no event and can raise no toast.
   */
  disabled?: boolean;
  title?: string;
}

export interface PaneOptGroup {
  label: string;
  options: PaneOption[];
}

export interface PaneSelectProps {
  /** DOM id, the `<label for>` target and the `data-paneselect` hook the e2e suite uses. */
  id: string;
  /**
   * The select's accessible name, and its caption unless `showLabel` is false.
   *
   * Hidden where the §F5.2 section heading above the control already says it — *Raster
   * layer* over a select captioned "Raster layer" is one line of sidebar spent twice. The
   * name itself never goes: it stays the `aria-label`, so the control is named for a screen
   * reader whether or not the sighted reader needs the caption repeated.
   */
  label: string;
  showLabel?: boolean;
  /** `''` — the placeholder option, which every one of these selects has. */
  value: string;
  /** What `''` says: "None", "Add an overlay…", "Pick the Y indicator…". */
  placeholder: string;
  groups: PaneOptGroup[];
  onChange: (value: string) => void;
  /** Rendered inside the control, left of the select — a domain dot, the contractual ★. */
  lead?: ReactNode;
  /** Label for `value` when no group offers it (see the header). */
  fallback?: string;
  disabled?: boolean;
}

export function PaneSelect({
  id,
  label,
  showLabel = true,
  value,
  placeholder,
  groups,
  onChange,
  lead,
  fallback,
  disabled,
}: PaneSelectProps) {
  const offered = groups.some((g) => g.options.some((o) => o.value === value));
  const current = groups.flatMap((g) => g.options).find((o) => o.value === value);
  return (
    <label className="pane-sel-row" htmlFor={id}>
      {showLabel ? <span className="pane-sel-label">{label}</span> : null}
      <span className="pane-sel-ctl">
        {lead}
        <select
          id={id}
          data-paneselect={id}
          className="std pane-sel"
          aria-label={label}
          value={value}
          disabled={disabled}
          // A 300 px sidebar ellipses a long indicator name; hover gives the whole of it
          // back. The open list — where a reader actually compares — is never truncated.
          //
          // The `fallback` case has to be spelled out rather than falling through to the
          // placeholder: a mapped indicator the domain rail has filtered out is rendered as
          // an option of its own (see the header), so the closed control reads its name while
          // `current` is undefined — and a tooltip saying "None" over it would be the same
          // lie about the map that the fallback option exists to prevent, moved onto hover.
          title={current?.label ?? (value ? (fallback ?? value) : placeholder)}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{placeholder}</option>
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  title={option.title}
                >
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
          {value && !offered ? <option value={value}>{fallback ?? value}</option> : null}
        </select>
      </span>
    </label>
  );
}

export default PaneSelect;
